import { apiFetch } from '@/api/request'
import type { ServerCart } from '@/api/mallCart'
import type { ApiResponse } from '@/types/verification'

export interface MallCartSetItem {
  productId: string
  name: string
  image: string
  optionName: string
  optionExtra: number
  qty: number
  price: number
  available: boolean
}

export interface MallCartSet {
  idx: number
  name: string
  itemCount: number
  createdAt: string
  updatedAt: string | null
  items?: MallCartSetItem[]
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const BASE = `${API_ORIGIN}/api/mall/cart-sets`

const fetchOptions: RequestInit = {
  credentials: 'include',
}

const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다.')
  }
  return (await response.json()) as ApiResponse<T>
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

export async function fetchCartSets() {
  const response = await apiFetch(BASE, fetchOptions)
  const result = await parseJson<{ sets: MallCartSet[] }>(response)
  return unwrap(result, '저장 목록을 불러오지 못했습니다.').sets
}

export async function createCartSet(payload: {
  name: string
  items: Array<{ productId: string; qty: number; optionName?: string; optionExtra?: number }>
}) {
  const response = await apiFetch(BASE, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ set: MallCartSet }>(response)
  return unwrap(result, '세트를 저장하지 못했습니다.').set
}

export async function deleteCartSet(idx: number) {
  const response = await apiFetch(`${BASE}/${idx}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '세트를 삭제하지 못했습니다.')
  }
}

export async function loadCartSet(idx: number, replace = false) {
  const response = await apiFetch(`${BASE}/${idx}/load`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ replace }),
  })
  const result = await parseJson<{ cart: ServerCart; set: MallCartSet; loadedCount: number }>(
    response,
  )
  if (!result.success || !result.data) {
    throw new Error(result.message || '세트를 불러오지 못했습니다.')
  }
  return { ...result.data, message: result.message || '' }
}
